import Button from '@/components/Button';
import EmptyBlock from '@/components/EmptyBlock';
import GsuLoader from '@/components/GsuLoader';
import IcoMoon from '@/components/IcoMoon';
import Layout, { Stack } from '@/components/Layout';
import LottieImage from '@/components/LottieImage';
import { Aside, AsideWrapper, Detail, DetailWrapper } from '@/components/ResponsivePage';
import Text from '@/components/Text';
import { PageMetaInfo } from '@/compositions/MetaInfo';
import { FragPageMetaInfo } from '@/compositions/MetaInfo/query';
import { Props as PageComposerProps } from '@/compositions/PageComposer';
import PageLayout from '@/compositions/PageLayout';
import { FragScheduleDay } from '@/compositions/ScheduleBlock/query';
import { coreLayoutContainerAside, coreLayoutSection, rbp } from '@/constants/measured-scope';
import { useAuth } from '@/context/auth';
import { useUI } from '@/context/ui';
import { getTzName } from '@/lib/datetime';
import { queryGsuApi } from '@/service/query';
import type { MetaInfo, Page, SchedulePageRequest, SchedulePageSection } from '@/shared/api-generated-types';
import { FragSessionCard } from '@/widgets/ClassCard/query';
import LoginSignUpPrompt from '@/widgets/LoginSignUpPrompt';
import Overlay from '@/widgets/Overlay';
import ScheduleFilters from '@/widgets/ScheduleFilters';
import { FragFacetInfo } from '@/widgets/ScheduleFilters/query';
import ScheduleFiltersMobile from '@/widgets/ScheduleFilters/ScheduleFiltersMobile';
import { useMediaQuery } from '@react-hookz/web';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { GetServerSideProps, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import { gql, useQuery } from 'urql';
import ScheduleMobile from './ScheduleMobile';
const PageComposerWithLoader = dynamic<PageComposerProps>(() => import('@/compositions/PageComposer'), {
  ssr: false,
  loading: () => <GsuLoader consumeSpace />
});
const QUERY_VARS = (startFrom: string, sessionsIntervalStart?: number, sessionsIntervalEnd?: number): Partial<SchedulePageRequest> => {
  dayjs.extend(relativeTime);
  let startTime = dayjs(startFrom);

  // If selected date is today.
  // Retrieve classes from current time onwards.
  // Eg, if its 3:30pm today, get classes from 3:30pm.
  if (dayjs().isSame(startFrom, 'day')) {
    // do nothing. retrieve classes from supplued startFrom.
  } else {
    // user selected new day. zero the hours, minutes, seconds to select classes from begging
    startTime = dayjs(startTime).set('hour', 0).set('minute', 0).set('second', 0);
  }
  return {
    timezone: getTzName(),
    startFrom: startTime.format(),
    // ensures ISO string
    sessionsIntervalStart,
    sessionsIntervalEnd
  };
};
const QUERY_VARS_MY_SCHEDULE = {
  timezone: getTzName(),
  startFrom: new Date().toISOString()
};
const QUERY_SCHEDULE = gql`
  ${FragSessionCard}
  ${FragScheduleDay}
  ${FragFacetInfo}
  ${FragPageMetaInfo}
  query SchedulePage($request: SchedulePageRequest!) {
    page: schedulePage(request: $request) {
      id
      title
      metaInfo {
        ...PageMetaInfo
      }
      sections {
        id
        ... on SchedulePageSection {
          pageInfo {
            totalItems
            hasPrevPage
            hasNextPage
            page
          }
          facetInfo {
            ...FacetInfo
          }
          items {
            ...ScheduleDay
          }
        }
      }
    }
  }
`;
const QUERY_MY_SCHEDULE = gql`
  ${FragSessionCard}
  ${FragScheduleDay}
  ${FragPageMetaInfo}
  query MySchedulePage($request: SchedulePageRequest!) {
    page: mySchedulePage(request: $request) {
      id
      title
      metaInfo {
        ...PageMetaInfo
      }
      sections {
        id
        ... on SchedulePageSection {
          items {
            ...ScheduleDay
          }
        }
      }
    }
  }
`;
const asideContainer = "a148n2dd";
const asideWrapper = "at5wc3b";
const asideDisabledContainer = "a1yluv0l";
const filtersButton = "f11sw4jv";
export const icon = "i1xxwpz";
export const iconAnimationContainer = "ip0qk4k";
export const iconAnimation = "i182nnss";

// Recognised slugs for this dynamic route
// Anything not recognised will trigger standard 404
const validScheduleTypes = ['browse', 'my-schedule'];
const ScheduleDesktop: NextPage<{
  metaInfo: MetaInfo;
}> = props => {
  const router = useRouter();
  const {
    t
  } = useTranslation('common');
  const isTablet = useMediaQuery(`only screen and (max-width : ${rbp.lg})`, {
    initializeWithValue: false
  });
  const {
    state: {
      user
    }
  } = useAuth();
  const {
    state: {
      schedulePage,
      scheduleFilters: scheduleFiltersState
    },
    dispatch
  } = useUI();
  const {
    scheduleType
  } = router.query;
  const whichTab = scheduleType === 'browse' ? ScheduleTab.BROWSE : ScheduleTab.MY_SCHEDULE;
  const isBrowseView = whichTab === ScheduleTab.BROWSE;
  const isMyScheduleView = whichTab === ScheduleTab.MY_SCHEDULE;
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
  const sliderMenuTransition = useTransition(filtersVisible, {
    from: {
      x: '100%',
      y: '0%',
      opacity: 1
    },
    enter: {
      x: '0%',
      y: '0%',
      position: 'absolute',
      right: 0,
      top: 112
    },
    leave: {
      x: '100%',
      y: '0%',
      opacity: 1
    }
  });

  // BrowseSchedule data
  const query_vars = useMemo(() => QUERY_VARS(schedulePage.dayString, schedulePage.sessionsIntervalStart, schedulePage.sessionsIntervalEnd), [schedulePage.day, schedulePage.sessionsIntervalStart, schedulePage.sessionsIntervalEnd]);
  const [{
    data: browseData,
    fetching: browseFetching
  }, executeQueryBrowse] = useQuery({
    query: QUERY_SCHEDULE,
    variables: {
      request: {
        ...query_vars,
        page: currentPage,
        facets: schedulePage.facets,
        maxItemsPerSection: 30
      }
    },
    // Only fetch browse schedule data if we're on the browse tab
    pause: whichTab !== ScheduleTab.BROWSE
  });
  const browsePageData = browseData?.page || {};

  // MySchedule data
  const [{
    data: myData,
    fetching: myFetching
  }] = useQuery({
    query: QUERY_MY_SCHEDULE,
    variables: {
      request: {
        ...QUERY_VARS_MY_SCHEDULE
      }
    },
    // Only fetch my schedule data if we're on the my-schedule tab
    pause: whichTab !== ScheduleTab.MY_SCHEDULE
  });
  const myPageData: Page = myData?.page || {};
  const hasBookedSessions = useMemo(() => (myPageData?.sections as SchedulePageSection[])?.[0]?.items?.length, [myData]);

  // Route flipping between Schedule & My Schedule
  const handleScheduleRoute = (index: number): void => {
    const destinationTab = index === ScheduleTab.BROWSE ? 'browse' : 'my-schedule';
    router.push(`/schedule/${destinationTab}`);
  };
  const toggleFilters = useCallback(() => {
    setFiltersVisible(s => !s);
  }, []);
  const handleFiltersChange = useCallback(() => {
    setCurrentPage(0);
    if (isTablet) {
      toggleFilters();
    }
  }, [isTablet]);

  // Pagination event handler
  const handleLoadMore = useCallback((idx: number, page: number) => {
    setCurrentPage(page);
    executeQueryBrowse({
      requestPolicy: 'cache-first'
    });
  }, []);

  // Loading facet filters only during the inital schedule sessions fetch from graphql
  useEffect(() => {
    try {
      if (!browseData || scheduleFiltersState.categories.length > 0 || scheduleFiltersState.guides.length > 0) return;
      const facets = browseData?.page?.sections[0]?.facetInfo.facetsAvailable;
      dispatch({
        type: 'SET_SCHEDULE_FILTERS',
        payload: {
          categories: facets.categories,
          guides: facets.guides
        }
      });
    } catch (error) {
      console.log('Issue getting filters', {
        error
      });
    }
  }, [isBrowseView, browseData]);
  const scheduleFilters = useMemo(() => {
    return isTablet ? <ScheduleFiltersMobile fetching={browseFetching} onFilterChange={handleFiltersChange} /> : <ScheduleFilters fetching={browseFetching} onFilterChange={handleFiltersChange} />;
  }, [browseFetching, isTablet]);

  // Data resets for scheduleType changes
  useEffect(() => {
    // TODO: Not ideal at all - We need to hack state reset changes in
    // with how we're handling tabbed content and queries here
    // It shouldn't be a blocker at the moment, but we'll want to revisit
    setCurrentPage(0);

    // Reset All filters on first load
    return () => {
      dispatch({
        type: 'RESET_ACTIVE_SCHEDULE_FILTERS'
      });
    };
  }, [scheduleType]);
  dayjs.extend(relativeTime);

  // Compute distinction when filters are applied for refreshing the results
  const distinction = useMemo<string>(() => {
    return `${[schedulePage.day, schedulePage.sessionsIntervalEnd, schedulePage.sessionsIntervalStart, ...schedulePage.facets].join('|')}`;
  }, [schedulePage.day, schedulePage.facets, schedulePage.sessionsIntervalEnd, schedulePage.sessionsIntervalStart]);
  return <PageLayout contentClassName="bg-white" footerVisible={false}>
      {props?.metaInfo ? <PageMetaInfo {...props.metaInfo} /> : null}
      <Stack justifyContent="end" className={`${filtersButton} hide-gte-lg`}>
        <div className={'flex-center gap-xs'} onClick={toggleFilters}>
          <IcoMoon size={18} icon="filters" />
          <Text size="sm">{t('search.filters')}</Text>
          {schedulePage.facets.length > 0 ? <span className={filterCount}>{schedulePage.facets.length}</span> : null}
        </div>
      </Stack>
      {sliderMenuTransition((style, item) => item && <>
              <animated.div style={{
        ...style,
        position: 'fixed',
        zIndex: 9,
        height: '100%'
      }}>
                {scheduleFilters}
              </animated.div>
              <Overlay zIndex={8} visible={filtersVisible} onClick={toggleFilters} />
            </>)}

      <section className={coreLayoutSection}>
        <div className={`${coreLayoutContainerAside} ${asideContainer} ${isMyScheduleView ? asideDisabledContainer : ''} relative`}>
          <DetailWrapper className="m-0 p-0 of-unset">
            <Detail className="p-0 of-unset">
              {/* BrowseSchedule data */}
              {isBrowseView && browsePageData && <PageComposerWithLoader {...browsePageData} fetching={browseFetching} onLoadMoreByPageNumber={handleLoadMore} distinction={distinction} />}

              {/* Show the following prompt only after the GQL query is complete to non-signed-in users */}
              {isMyScheduleView && !user && !myFetching && <LoginSignUpPrompt title={t('mySchedule.prompt')} caption={t('mySchedule.caption')} ctaText={t('mySchedule.ctaText')} />}
              {/* For signed-in users, either show their booked sessions OR empty screen when they've no upcoming sessions */}
              {isMyScheduleView && user && <>
                  {myFetching || hasBookedSessions ? <div className="p-lg-gte-desktop">
                      <PageComposerWithLoader {...myPageData} fetching={myFetching} />
                    </div> : <EmptyBlock>
                      <Layout>
                        <Stack y alignItems="center" justifyContent="space-between">
                          <div className={`${iconAnimationContainer} mb-sm`}>
                            <LottieImage autoplay loop className={iconAnimation} name="calendar-animation" path={'https://ddq2bomue87t4.cloudfront.net/dialog-icons/calendar.json'} />
                          </div>
                          <Text size="lg" tag="h4" responsiveSize={{
                      sm: 'md',
                      md: 'lg'
                    }} weight="lg" className="flex-center mb-xxxs">
                            {t('mySchedule.noSessions')}
                          </Text>
                          <Text size="xs" weight="sm" tag="p">
                            {t('mySchedule.noSessionsSubtitle')}
                          </Text>
                          <Button label={t('mySchedule.browseClasses')} theme="primary" size="md" onClick={() => handleScheduleRoute(0)} className="mt-md" />
                        </Stack>
                      </Layout>
                    </EmptyBlock>}
                </>}
            </Detail>
          </DetailWrapper>
          {isBrowseView && <AsideWrapper className={`${asideWrapper} hide-lte-tablet`}>
              <Aside>{scheduleFilters}</Aside>
            </AsideWrapper>}
        </div>
      </section>
    </PageLayout>;
};
export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en-US',
  query,
  res,
  req
}) => {
  if (query && !validScheduleTypes.includes(`${query?.scheduleType}`)) {
    res.statusCode = 404;
    return {
      notFound: true
    };
  }
  const [urqlState,, data] = await queryGsuApi(query?.scheduleType === 'browse' ? QUERY_SCHEDULE : QUERY_MY_SCHEDULE, {
    request: {
      ...QUERY_VARS(new Date().toISOString()),
      page: 0
    }
  }, req);
  return {
    props: {
      ...(await serverSideTranslations(locale, ['badge', 'common', 'classes'])),
      urqlState,
      metaInfo: data?.page?.metaInfo || ''
    }
  };
};
enum ScheduleTab {
  BROWSE,
  MY_SCHEDULE,
}
const Schedule: NextPage<{
  metaInfo: MetaInfo;
}> = props => {
  const isSmallDevice = useMediaQuery(`only screen and (max-width : ${rbp.smallTablet})`, {
    initializeWithValue: false
  });
  const component = useMemo(() => {
    if (isSmallDevice) {
      return <ScheduleMobile {...props} />;
    }
    return <ScheduleDesktop {...props} />;
  }, [isSmallDevice]);
  return component;
};
export default Schedule;
const filterCount = "f8udkgn";

require("./index.linaria.module.css!=!../../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");